import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { nsConnectMessage } from './NetSuiteConnect';

// Master-admin SKU workbench: upload an Excel of SKUs, match each against
// NetSuite, attach datasheets and manage vendor pricing in one grid.
export default function SKUsView({ toast }) {
  const [rows, setRows] = useState([]);
  const [listId, setListId] = useState(null);
  const [listName, setListName] = useState('');
  const [lists, setLists] = useState([]);
  const [progress, setProgress] = useState(null); // {label, done, total}
  const [dsSearch, setDsSearch] = useState(null); // {idx, q, hits}
  const [records, setRecords] = useState(null); // rich Grace-template records
  const [vendor, setVendor] = useState({}); // batch vendor for NetSuite item creation
  const [vhits, setVhits] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { api.skusLists().then(r => setLists(r.items)).catch(() => {}); }, []);

  async function onUpload(file) {
    if (!file) return;
    setProgress({ label: 'Reading spreadsheet…', done: 0, total: 1 });
    try {
      const parsed = await api.skusParse(file);
      const recs = parsed.records || [];
      if (!recs.length) { toast('No SKUs found in that file', true); setProgress(null); return; }
      setRecords(parsed.format === 'grace' ? recs : null);
      if (parsed.duplicates?.length) toast(`${parsed.duplicates.length} duplicate SKU(s) in the file were skipped: ${parsed.duplicates.slice(0, 3).join(', ')}`, true);
      if (parsed.flagged_count) toast(`${parsed.flagged_count} SKU(s) have discrepancies — check the ⚠ flags`, true);
      setListName(file.name.replace(/\.[^.]+$/, ''));
      setListId(null);
      // Build the grid straight from the spreadsheet so all info shows immediately.
      const out = recs.map(rec => ({
        sku: rec.sku,
        displayname: rec.display_name || rec.sku,
        description: rec.description || '',
        sheet_price: rec.vendor_price || '',
        vendor: rec.vendor || '',
        flags: rec.flags || [],
        found: false, ns_item_id: null, itemid: '',
        datasheet_id: null, datasheet_name: '', cost: null, new_cost: rec.vendor_price || '',
      }));
      setRows(out);
      // Now annotate NetSuite matches in the background, in chunks of 50.
      const skus = recs.map(r => r.sku);
      const chunks = [];
      for (let i = 0; i < skus.length; i += 50) chunks.push(skus.slice(i, i + 50));
      const est = Math.ceil(chunks.length * 6);
      for (let ci = 0; ci < chunks.length; ci++) {
        setProgress({ label: `Checking ${skus.length} SKUs against NetSuite… ~${Math.max(1, est - ci * 6)}s left`, done: ci, total: chunks.length });
        const { rows: matched } = await api.skusMatch(chunks[ci]);
        const bySku = {};
        matched.forEach(m => { bySku[m.sku] = m; });
        setRows(prev => prev.map(r => bySku[r.sku]
          ? { ...r, found: bySku[r.sku].found, ns_item_id: bySku[r.sku].ns_item_id, itemid: bySku[r.sku].itemid }
          : r));
      }
      setProgress(null);
    } catch (e) {
      setProgress(null);
      toast('Import failed: ' + (e.response?.data?.detail || e.message), true);
    }
  }

  async function createDatasheets() {
    if (!records?.length) return;
    setProgress({ label: 'Creating family datasheets…', done: 0, total: 1 });
    try {
      const { created, skipped, sku_map } = await api.skusCreateDatasheets(records);
      // Attach each created/existing datasheet back onto its SKU rows.
      if (sku_map) {
        setRows(prev => prev.map(r => sku_map[r.sku]
          ? { ...r, datasheet_id: sku_map[r.sku].id, datasheet_name: sku_map[r.sku].name }
          : r));
      }
      setProgress(null);
      if (created.length) toast(`Created ${created.length} family datasheet(s): ${created.map(c => `${c.name} (${c.code}, ${c.variants} variants)`).join(', ')} — attached to their SKUs. Review them in the Editor.`);
      else toast(skipped.length ? `Datasheet(s) already existed and were attached: ${skipped.join(', ')}` : 'Nothing to create', true);
    } catch (e) {
      setProgress(null);
      toast('Create failed: ' + (e.response?.data?.detail || e.message), true);
    }
  }

  async function copyVendorFromItem(item) {
    try {
      const info = await api.nsItemFields(item.id);
      const v = info.vendor || {};
      if (!v.vendor_id) { toast('That item has no vendor line to copy.', true); return; }
      setVendor({ vendor_id: v.vendor_id, vendor_name: v.vendor_name || ('Vendor #' + v.vendor_id), subsidiary_id: v.subsidiary_id });
      setVhits(null);
      toast('Vendor set for new items: ' + (v.vendor_name || v.vendor_id));
    } catch (e) { toast('Could not read vendor: ' + (e.response?.data?.detail || e.message), true); }
  }

  async function searchVendorItem(q) {
    if (!q.trim()) { setVhits(null); return; }
    try { const { items } = await api.nsSearch(q.trim()); setVhits(items || []); }
    catch (e) { setVhits([]); }
  }

  async function pushToNetsuite(idx) {
    const r = rows[idx];
    const rec = (records || []).find(x => x.sku === r.sku);
    if (!rec) { toast('No spreadsheet data for this SKU', true); return; }
    if (!vendor.vendor_id) { toast('Set a vendor for new items first (top of page)', true); return; }
    patch(idx, { pushing: true, push_error: '' });
    try {
      const res = await api.skusPushNetsuite({
        record: rec,
        vendor: { vendor_id: vendor.vendor_id, subsidiary_id: vendor.subsidiary_id, purchase_price: r.new_cost ? Number(r.new_cost) : undefined },
        datasheet_link: r.datasheet_id ? (window.location.origin + '/editor?product=' + r.datasheet_id) : undefined,
      });
      patch(idx, { pushing: false, found: true, ns_item_id: res.id, itemid: r.sku, pushed: true });
      toast(`${r.sku} created in NetSuite`);
    } catch (e) {
      patch(idx, { pushing: false, push_error: e.response?.data?.detail || e.message });
      toast(`${r.sku}: ${e.response?.data?.detail || e.message}`, true);
    }
  }

  async function pushAllNew() {
    const targets = rows.map((r, i) => ({ r, i })).filter(x => !x.r.found && !x.r.pushed);
    if (!targets.length) { toast('No new SKUs to push — all are already in NetSuite'); return; }
    if (!vendor.vendor_id) { toast('Set a vendor for new items first (top of page)', true); return; }
    for (let k = 0; k < targets.length; k++) {
      setProgress({ label: `Creating items in NetSuite ${k + 1}/${targets.length}…`, done: k, total: targets.length });
      await pushToNetsuite(targets[k].i);
    }
    setProgress(null);
    toast('Finished pushing new SKUs to NetSuite');
  }

  async function pushPrice(idx) {
    const r = rows[idx];
    if (!r.ns_item_id || !r.new_cost) { toast('Enter the new cost first', true); return; }
    try {
      await api.nsUpdatePricing({ item_id: r.ns_item_id, purchase_price: Number(r.new_cost) });
      patch(idx, { cost: r.new_cost, new_cost: '' });
      toast('Price updated in NetSuite');
    } catch (e) {
      toast(nsConnectMessage(e) || e.response?.data?.detail || e.message, true);
    }
  }

  function patch(idx, p) { setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...p } : r)); }

  async function searchDs(idx, q) {
    setDsSearch({ idx, q, hits: null });
    if (!q.trim()) return;
    try {
      const { items } = await api.listProducts({ q: q.trim(), limit: 6 });
      setDsSearch(s => (s && s.idx === idx ? { ...s, hits: items } : s));
    } catch (e) { /* ignore */ }
  }

  async function saveList() {
    if (!rows.length) return;
    try {
      const doc = await api.skusSave({ id: listId, name: listName || 'SKU list', rows });
      setListId(doc.id);
      setLists(ls => [doc, ...ls.filter(l => l.id !== doc.id)]);
      toast('SKU list saved');
    } catch (e) { toast('Save failed: ' + (e.response?.data?.detail || e.message), true); }
  }

  function openList(l) { setRows(l.rows || []); setListId(l.id); setListName(l.name); }

  const matched = rows.filter(r => r.found).length;

  return (
    <div className="pc-view" data-testid="skus-view">
      <div className="pc-head">
        <div>
          <h2>SKU's</h2>
          <p className="pc-sub">Upload an Excel or CSV of SKUs — all product info (display name, description, vendor cost, specs) is read straight from the sheet. Each SKU is also checked against NetSuite so you can attach datasheets, push vendor costs and turn them into family datasheets, all in one place. Lists save so you can come back later.</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label className="btn btn-primary" data-testid="skus-upload-btn">
          Upload SKU spreadsheet (.xlsx / .csv)
          <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }}
            onChange={e => { onUpload(e.target.files[0]); e.target.value = ''; }} />
        </label>
        {rows.length > 0 && (
          <>
            <input className="input" data-testid="skus-list-name" style={{ maxWidth: 220 }} value={listName} onChange={e => setListName(e.target.value)} placeholder="List name" />
            <button className="btn" data-testid="skus-save-btn" onClick={saveList}>Save list</button>
            {records?.length > 0 && <button className="btn btn-primary" data-testid="skus-create-datasheets" onClick={createDatasheets} disabled={!!progress} title="Group these SKUs by family/series and create family datasheets with variant tables">Create datasheets ({records.length} SKUs)</button>}
            <button className="btn" data-testid="skus-push-all" onClick={pushAllNew} disabled={!!progress} title="Create every SKU not already in NetSuite as a new inventory item">Push all new → NetSuite</button>
            <span className="field-hint" data-testid="skus-matched-count">{matched}/{rows.length} in NetSuite</span>
          </>
        )}
        {lists.length > 0 && (
          <select className="select" data-testid="skus-list-picker" style={{ maxWidth: 240 }} value={listId || ''}
            onChange={e => { const l = lists.find(x => x.id === e.target.value); if (l) openList(l); }}>
            <option value="">Open a saved list…</option>
            {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({(l.rows || []).length})</option>)}
          </select>
        )}
      </div>
      {rows.length > 0 && (
        <div className="skus-vendor-bar" data-testid="skus-vendor-bar">
          <span className="field-hint">Vendor for new NetSuite items:</span>
          <input className="input" data-testid="skus-vendor-id" style={{ maxWidth: 150 }} placeholder="Vendor internal ID"
            value={vendor.vendor_id || ''} onChange={e => setVendor(v => ({ ...v, vendor_id: e.target.value, vendor_name: '' }))} />
          <input className="input" data-testid="skus-vendor-sub" style={{ maxWidth: 120 }} placeholder="Subsidiary ID"
            value={vendor.subsidiary_id || ''} onChange={e => setVendor(v => ({ ...v, subsidiary_id: e.target.value }))} />
          {vendor.vendor_name && <span className="pc-ns-pill found" data-testid="skus-vendor-name">{vendor.vendor_name}</span>}
          <div style={{ position: 'relative' }}>
            <input className="input" data-testid="skus-vendor-copy-search" style={{ maxWidth: 220 }} placeholder="…or copy vendor from an item"
              onChange={e => searchVendorItem(e.target.value)} />
            {vhits && vhits.length > 0 && (
              <div className="skus-ds-pop" data-testid="skus-vendor-copy-pop">
                {vhits.slice(0, 6).map(h => (
                  <button key={h.id} className="nsp-search-hit" onClick={() => copyVendorFromItem(h)}>
                    <strong>{h.itemid || h.itemId}</strong><span>{h.displayname || h.displayName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {progress && (
        <div className="skus-progress" data-testid="skus-progress">
          <div className="skus-progress-bar"><span style={{ width: (progress.total ? Math.round(progress.done / progress.total * 100) : 5) + '%' }} /></div>
          <span>{progress.label}</span>
        </div>
      )}
      {rows.length === 0 && !progress && (
        <div className="empty-note" data-testid="skus-empty">No SKUs loaded yet. Upload a spreadsheet — the SKU column is detected automatically (headers like "SKU", "Item", "Code"), or the first column is used.</div>
      )}
      {rows.length > 0 && (
        <div className="pc-table-wrap">
          <table className="pc-table skus-table" data-testid="skus-table">
            <thead>
              <tr><th>#</th><th>SKU</th><th>NetSuite</th><th>Display name</th><th>Description</th><th>Vendor cost (sheet)</th><th>New cost → NS</th><th></th><th>Datasheet</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.sku + i} data-testid={'sku-row-' + i}>
                  <td>{i + 1}</td>
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{r.sku}</td>
                  <td>
                    <span className={'pc-ns-pill' + (r.found ? ' found' : '')} data-testid={'sku-status-' + i}>
                      {r.found ? 'Matched' : 'Not found'}
                    </span>
                    {(r.flags || []).length > 0 && <span className="skus-flag-pill" data-testid={'sku-flags-' + i} title={r.flags.join('\n')}>⚠ {r.flags.length}</span>}
                  </td>
                  <td style={{ minWidth: 160 }}>{r.displayname}</td>
                  <td style={{ minWidth: 200, fontSize: 11.5 }}>{r.description}</td>
                  <td className="num" data-testid={'sku-cost-' + i}>
                    {r.sheet_price ? <span title="From the spreadsheet">{r.sheet_price}</span> : '—'}
                  </td>
                  <td>{r.found
                    ? <input className="input" data-testid={'sku-new-cost-' + i} style={{ width: 84 }} type="number" step="0.01" value={r.new_cost} onChange={e => patch(i, { new_cost: e.target.value })} />
                    : <input className="input" data-testid={'sku-new-cost-' + i} style={{ width: 84 }} type="number" step="0.01" value={r.new_cost} title="Cost that will be set on the new NetSuite item" onChange={e => patch(i, { new_cost: e.target.value })} />}</td>
                  <td>
                    {r.found
                      ? <button className="btn btn-sm" data-testid={'sku-push-price-' + i} onClick={() => pushPrice(i)}>Update price</button>
                      : (r.pushed
                        ? <span className="pc-ns-pill found" data-testid={'sku-pushed-' + i}>✓ Created</span>
                        : <button className="btn btn-sm" data-testid={'sku-push-item-' + i} disabled={r.pushing} onClick={() => pushToNetsuite(i)} title="Create this SKU as a new inventory item in NetSuite">{r.pushing ? 'Pushing…' : 'Push → NS'}</button>)}
                    {r.push_error && <div className="field-hint" data-testid={'sku-push-error-' + i} style={{ color: '#b3261e', maxWidth: 160 }}>{r.push_error}</div>}
                  </td>
                  <td style={{ minWidth: 220, position: 'relative' }}>
                    {r.datasheet_id ? (
                      <span className="pc-ds-pill found" data-testid={'sku-ds-' + i}>
                        {r.datasheet_name}
                        <button className="pc-ds-clear" title="Detach" onClick={() => patch(i, { datasheet_id: null, datasheet_name: '' })}>✕</button>
                      </span>
                    ) : (
                      <>
                        <input className="input" data-testid={'sku-ds-search-' + i} placeholder="Search datasheets…"
                          value={dsSearch && dsSearch.idx === i ? dsSearch.q : ''}
                          onChange={e => searchDs(i, e.target.value)} />
                        {dsSearch && dsSearch.idx === i && dsSearch.hits && dsSearch.hits.length === 0 && dsSearch.q.trim() && (
                          <div className="field-hint" data-testid={'sku-ds-none-' + i}>No datasheets found in this region</div>
                        )}
                        {dsSearch && dsSearch.idx === i && dsSearch.hits && dsSearch.hits.length > 0 && (
                          <div className="skus-ds-pop" data-testid={'sku-ds-pop-' + i}>
                            {dsSearch.hits.map(h => (
                              <button key={h.id} className="nsp-search-hit" onClick={() => { patch(i, { datasheet_id: h.id, datasheet_name: h.name || h.code }); setDsSearch(null); }}>
                                <strong>{h.name}</strong><span>{h.code}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
